<div *ngIf="isVisible" class="col-span-5 bg-white dark:bg-indigo-custom rounded-xl p-3 md:p-6">

  <!-- Header -->
  <div class="mb-4">
    <div class="flex items-center space-x-3 mb-1">
      <h2 class="text-secondary dark:text-white text-lg tracking-wide font-semibold">Your first leads (up to 10)</h2>

      <span *ngIf="isGenerating"
            class="inline-flex items-center space-x-2 rounded-full bg-blue-royal/10 px-3 py-1 text-xs font-medium text-blue-royal">
        <span class="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-royal"></span>
        <span>Generating</span>
      </span>
    </div>

    <p class="text-secondary dark:text-primary-200 text-sm">
      <ng-container *ngIf="isGenerating">
        We're matching companies to your product. Leads appear here as soon as they're found.
      </ng-container>
      <ng-container *ngIf="!isGenerating">
        The first leads matched to the product and targeting you set up during onboarding.
      </ng-container>
    </p>
  </div>
  <!-- / Header -->

  <!-- Table -->
  <div *ngIf="hasLeads"
       class="rounded-xl border border-gray-100 dark:border-secondary-800 overflow-hidden">
    <div class="overflow-x-auto">
      <table class="w-full text-left text-sm">
        <thead class="bg-gray-50 dark:bg-secondary-800">
        <tr>
          <th scope="col" class="px-4 py-3.5 text-sm font-semibold text-gray-700 dark:text-primary-200 whitespace-nowrap">Contact</th>
          <th scope="col" class="px-4 py-3.5 text-sm font-semibold text-gray-700 dark:text-primary-200 whitespace-nowrap">Job title</th>
          <th scope="col" class="px-4 py-3.5 text-sm font-semibold text-gray-700 dark:text-primary-200 whitespace-nowrap">Company</th>
          <th scope="col" class="px-4 py-3.5 text-sm font-semibold text-gray-700 dark:text-primary-200 whitespace-nowrap">Industry</th>
          <th scope="col" class="px-4 py-3.5 text-sm font-semibold text-gray-700 dark:text-primary-200 whitespace-nowrap">Location</th>
          <th scope="col" class="px-4 py-3.5 text-sm font-semibold text-gray-700 dark:text-primary-200 whitespace-nowrap">Phone &amp; Email</th>
        </tr>
        </thead>
        <tbody>
        <tr *ngFor="let lead of visibleLeads; trackBy: trackById"
            class="border-t border-gray-100 dark:border-secondary-800">

          <!-- Contact -->
          <td class="px-4 py-3.5">
            <div class="flex items-center space-x-3">

              <img *ngIf="photoUrl(lead)"
                   [src]="photoUrl(lead)"
                   [alt]="displayName(lead)"
                   (error)="onPhotoError(lead)"
                   referrerpolicy="no-referrer"
                   loading="lazy"
                   class="h-9 w-9 shrink-0 rounded-full object-cover bg-gray-100 dark:bg-secondary-800" />

              <span *ngIf="!photoUrl(lead)"
                    aria-hidden="true"
                    class="h-9 w-9 shrink-0 rounded-full bg-gray-100 dark:bg-secondary-800 flex items-center justify-center text-xs font-semibold text-gray-500 dark:text-primary-200">
                {{ initials(lead) }}
              </span>

              <span class="font-medium text-secondary dark:text-white whitespace-nowrap">{{ displayName(lead) }}</span>

              <a *ngIf="lead.person_linkedin_url"
                 [href]="lead.person_linkedin_url"
                 target="_blank"
                 rel="noopener noreferrer"
                 title="Open LinkedIn profile"
                 class="text-gray-400 hover:text-blue-royal">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5C0 2.12 1.12 1 2.5 1S4.98 2.12 4.98 3.5zM.24 8.02h4.5V24H.24V8.02zM8.24 8.02h4.31v2.18h.06c.6-1.14 2.07-2.34 4.26-2.34 4.56 0 5.4 3 5.4 6.9V24h-4.5v-7.4c0-1.77-.03-4.05-2.47-4.05-2.47 0-2.85 1.93-2.85 3.92V24h-4.5V8.02z"/>
                </svg>
              </a>
            </div>
          </td>

          <!-- Job title -->
          <td class="px-4 py-3.5 text-secondary dark:text-primary-200">
            {{ lead.person_job_title || '—' }}
          </td>

          <!-- Company -->
          <td class="px-4 py-3.5">
            <div class="text-secondary dark:text-white whitespace-nowrap">{{ lead.company_name || '—' }}</div>
            <div class="flex items-center space-x-2 text-xs text-gray-500 dark:text-primary-200">
              <a *ngIf="lead.company_domain"
                 [href]="'https://' + lead.company_domain"
                 target="_blank"
                 rel="noopener noreferrer"
                 class="hover:text-blue-royal">
                {{ lead.company_domain }}
              </a>
              <span *ngIf="employeeCount(lead)">{{ employeeCount(lead) }} employees</span>
            </div>
          </td>

          <!-- Industry -->
          <td class="px-4 py-3.5 text-secondary dark:text-primary-200">
            {{ lead.company_industry || '—' }}
          </td>

          <!-- Location -->
          <td class="px-4 py-3.5 text-secondary dark:text-primary-200">
            {{ lead.person_location || lead.company_location || '—' }}
          </td>

          <!-- Phone & Email -->
          <td class="px-4 py-3.5 whitespace-nowrap">
            <div class="flex flex-col items-start gap-1.5">

              <!-- Email: unlocked -->
              <span *ngIf="emailStatus(lead) === 'unlocked'"
                    class="inline-flex items-center gap-1 text-sm text-gray-700 dark:text-gray-200">
                <span class="h-2 w-2 rounded-full bg-green-500"></span>
                {{ lead.person_email }}
              </span>

              <!-- Email: locked → same credit confirm as pipeline persons -->
              <button *ngIf="emailStatus(lead) === 'locked'"
                      type="button"
                      (click)="startUnlock(lead)"
                      [disabled]="isUnlocking(lead)"
                      class="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-500 disabled:opacity-60">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
                <span *ngIf="!isUnlocking(lead)">{{ 'lead_pipelines.detail.persons.unlock' | translate }}</span>
                <span *ngIf="isUnlocking(lead)">…</span>
              </button>

              <!-- Email: not found -->
              <span *ngIf="emailStatus(lead) === 'not_found'"
                    class="text-xs text-gray-400">
                {{ 'lead_pipelines.email_status.not_found' | translate }}
              </span>

              <!-- Report actions (ICP-gated) -->
              <button *ngIf="isIcpOptimized && !isEnhanced(lead) && !isEnhancing(lead)"
                      type="button"
                      (click)="startGenerateReport(lead)"
                      class="inline-flex items-center gap-1 text-xs font-medium text-blue-royal hover:text-indigo-500">
                Generate report
              </button>

              <span *ngIf="isEnhancing(lead)"
                    class="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500">
                <span class="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-royal"></span>
                Generating...
              </span>

              <button *ngIf="isEnhanced(lead) && !isEnhancing(lead)"
                      type="button"
                      (click)="onOpenReportClick(lead)"
                      class="inline-flex items-center gap-1 text-xs font-medium text-green-700 hover:text-green-600">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                Open report
              </button>

            </div>
          </td>

        </tr>
        </tbody>
      </table>
    </div>

    <!-- Fold toggle -->
    <button *ngIf="isFoldable"
            type="button"
            (click)="toggleExpanded()"
            [attr.aria-expanded]="isExpanded"
            class="w-full flex items-center justify-center space-x-2 border-t border-gray-100 dark:border-secondary-800 bg-gray-50 dark:bg-secondary-800 px-4 py-3 text-sm font-medium text-primary dark:text-primary-200 hover:opacity-90">
      <span *ngIf="!isExpanded">View all {{ leads.length }} leads</span>
      <span *ngIf="isExpanded">Show fewer leads</span>

      <svg class="w-4 h-4 transition-transform duration-200"
           [class.rotate-180]="isExpanded"
           fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
      </svg>
    </button>
    <!-- / Fold toggle -->

  </div>
  <!-- / Table -->

  <!-- Skeleton table while generating and no leads yet -->
  <div *ngIf="!hasLeads && (isGenerating || isLoading)"
       class="rounded-xl border border-gray-100 dark:border-secondary-800 overflow-hidden">
    <div class="overflow-x-auto">
      <table class="w-full text-left text-sm">
        <thead class="bg-gray-50 dark:bg-secondary-800">
        <tr>
          <th scope="col" class="px-4 py-3.5 text-sm font-semibold text-gray-700 dark:text-primary-200 whitespace-nowrap">Contact</th>
          <th scope="col" class="px-4 py-3.5 text-sm font-semibold text-gray-700 dark:text-primary-200 whitespace-nowrap">Job title</th>
          <th scope="col" class="px-4 py-3.5 text-sm font-semibold text-gray-700 dark:text-primary-200 whitespace-nowrap">Company</th>
          <th scope="col" class="px-4 py-3.5 text-sm font-semibold text-gray-700 dark:text-primary-200 whitespace-nowrap">Industry</th>
          <th scope="col" class="px-4 py-3.5 text-sm font-semibold text-gray-700 dark:text-primary-200 whitespace-nowrap">Location</th>
          <th scope="col" class="px-4 py-3.5 text-sm font-semibold text-gray-700 dark:text-primary-200 whitespace-nowrap">Phone &amp; Email</th>
        </tr>
        </thead>
        <tbody>
        <tr *ngFor="let row of skeletonRows"
            class="border-t border-gray-100 dark:border-secondary-800 animate-pulse">
          <td class="px-4 py-3.5">
            <div class="flex items-center space-x-3">
              <span class="h-9 w-9 shrink-0 rounded-full bg-gray-200 dark:bg-secondary-700"></span>
              <span class="h-4 w-28 rounded bg-gray-200 dark:bg-secondary-700"></span>
            </div>
          </td>
          <td class="px-4 py-3.5">
            <span class="inline-block h-4 w-24 rounded bg-gray-200 dark:bg-secondary-700"></span>
          </td>
          <td class="px-4 py-3.5">
            <span class="inline-block h-4 w-32 rounded bg-gray-200 dark:bg-secondary-700 mb-1"></span>
            <span class="inline-block h-3 w-20 rounded bg-gray-100 dark:bg-secondary-700"></span>
          </td>
          <td class="px-4 py-3.5">
            <span class="inline-block h-4 w-20 rounded bg-gray-200 dark:bg-secondary-700"></span>
          </td>
          <td class="px-4 py-3.5">
            <span class="inline-block h-4 w-24 rounded bg-gray-200 dark:bg-secondary-700"></span>
          </td>
          <td class="px-4 py-3.5">
            <span class="inline-block h-4 w-16 rounded bg-gray-200 dark:bg-secondary-700"></span>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <div class="flex items-center justify-center border-t border-gray-100 dark:border-secondary-800 bg-gray-50 dark:bg-secondary-800 px-4 py-3">
      <p class="text-sm text-gray-500 dark:text-primary-200">Finding your first leads — this usually takes a couple of minutes.</p>
    </div>
  </div>
  <!-- / Skeleton table -->

  <!-- Modals -->
  <app-unlock-email-confirm-modal *ngIf="unlockTarget"
                                  [personName]="displayName(unlockTarget)"
                                  (confirm)="confirmUnlock()"
                                  (closed)="unlockTarget = null">
  </app-unlock-email-confirm-modal>
  <app-open-report-modal *ngIf="showOpenReportModal"
                         [leadName]="openReportLeadName"
                         (confirm)="confirmOpenReport()"
                         (closed)="showOpenReportModal = false">
  </app-open-report-modal>
  <app-generate-report-confirm-modal *ngIf="generateReportTarget"
                                     [leadName]="displayName(generateReportTarget)"
                                     (confirm)="confirmGenerateReport()"
                                     (closed)="generateReportTarget = null">
  </app-generate-report-confirm-modal>

</div>
